import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Alert, Row, Col, Tabs, Tab } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { apiClient, API_ENDPOINTS } from '../utils/apiUtils';

const WorkPlanModal = ({ show, onHide, workPlan = null, onSuccess }) => {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('basic');
  const [signatoryPrefs, setSignatoryPrefs] = useState({
    prepared_by_enabled: true,
    checked_and_verified_enabled: true,
    approved_by_enabled: true
  });

  const toggleSignatoryPref = (key) => {
    setSignatoryPrefs((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const [form, setForm] = useState({
    title: '',
    total_duration: '',
    planned_start: '',
    planned_end: '',
    status: ''
  });

  const [planningRows, setPlanningRows] = useState([
    {
      id: 1,
      objectives: '',
      expected_output: '',
      activities: '',
      months: {
        M1: false, M2: false, M3: false, M4: false, M5: false, M6: false,
        M7: false, M8: false, M9: false, M10: false, M11: false, M12: false
      }
    },
    {
      id: 2,
      objectives: '',
      expected_output: '',
      activities: '',
      months: {
        M1: false, M2: false, M3: false, M4: false, M5: false, M6: false,
        M7: false, M8: false, M9: false, M10: false, M11: false, M12: false
      }
    },
    {
      id: 3,
      objectives: '',
      expected_output: '',
      activities: '',
      months: {
        M1: false, M2: false, M3: false, M4: false, M5: false, M6: false,
        M7: false, M8: false, M9: false, M10: false, M11: false, M12: false
      }
    },
    {
      id: 4,
      objectives: '',
      expected_output: '',
      activities: '',
      months: {
        M1: false, M2: false, M3: false, M4: false, M5: false, M6: false,
        M7: false, M8: false, M9: false, M10: false, M11: false, M12: false
      }
    },
    {
      id: 5,
      objectives: '',
      expected_output: '',
      activities: '',
      months: {
        M1: false, M2: false, M3: false, M4: false, M5: false, M6: false,
        M7: false, M8: false, M9: false, M10: false, M11: false, M12: false
      }
    }
  ]);

  // Reset form when modal opens/closes or workPlan changes
  useEffect(() => {
    if (show) {
      setActiveTab('basic');
      if (workPlan) {
        // Load existing work plan data for editing
        loadWorkPlanData(workPlan.work_plan_id);
      } else {
        // Reset to default for new work plan
        resetForm();
      }
    }
  }, [show, workPlan]);

  const loadWorkPlanData = async (id) => {
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await apiClient.get(`${API_ENDPOINTS.WORK_PLANS}/${id}`);
      setForm({
        title: data.title || '',
        total_duration: data.total_duration || '',
        planned_start: data.planned_start ? data.planned_start.substring(0, 10) : '',
        planned_end: data.planned_end ? data.planned_end.substring(0, 10) : '',
        status: data.status || 'Pending'
      });

      // Load signatory enable/disable prefs (defaults to enabled)
      setSignatoryPrefs({
        prepared_by_enabled: data.prepared_by_enabled !== 0 && data.prepared_by_enabled !== '0' && data.prepared_by_enabled !== false,
        checked_and_verified_enabled: data.checked_and_verified_enabled !== 0 && data.checked_and_verified_enabled !== '0' && data.checked_and_verified_enabled !== false,
        approved_by_enabled: data.approved_by_enabled !== 0 && data.approved_by_enabled !== '0' && data.approved_by_enabled !== false
      });
      if (Array.isArray(data.planning_rows) && data.planning_rows.length) {
        const rows = data.planning_rows.map((r, idx) => ({
          id: idx + 1,
          objectives: r.objectives || '',
          expected_output: r.expected_output || '',
          activities: r.activities || '',
          months: r.months || {
            M1: false, M2: false, M3: false, M4: false, M5: false, M6: false,
            M7: false, M8: false, M9: false, M10: false, M11: false, M12: false
          }
        }));
        
        // Ensure at least 5 default rows
        if (rows.length < 5) {
          const defaultRows = Array.from({ length: 5 - rows.length }, (_, i) => ({
            id: rows.length + i + 1,
            objectives: '',
            expected_output: '',
            activities: '',
            months: {
              M1: false, M2: false, M3: false, M4: false, M5: false, M6: false,
              M7: false, M8: false, M9: false, M10: false, M11: false, M12: false
            }
          }));
          setPlanningRows([...rows, ...defaultRows]);
        } else {
          setPlanningRows(rows);
        }
      }
    } catch (err) {
      console.error('Failed to load work plan', err);
      setError('Failed to load work plan.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      title: '',
      total_duration: '',
      planned_start: '',
      planned_end: '',
      status: 'Pending'
    });
    setPlanningRows([
      {
        id: 1,
        objectives: '',
        expected_output: '',
        activities: '',
        months: {
          M1: false, M2: false, M3: false, M4: false, M5: false, M6: false,
          M7: false, M8: false, M9: false, M10: false, M11: false, M12: false
        }
      },
      {
        id: 2,
        objectives: '',
        expected_output: '',
        activities: '',
        months: {
          M1: false, M2: false, M3: false, M4: false, M5: false, M6: false,
          M7: false, M8: false, M9: false, M10: false, M11: false, M12: false
        }
      },
      {
        id: 3,
        objectives: '',
        expected_output: '',
        activities: '',
        months: {
          M1: false, M2: false, M3: false, M4: false, M5: false, M6: false,
          M7: false, M8: false, M9: false, M10: false, M11: false, M12: false
        }
      },
      {
        id: 4,
        objectives: '',
        expected_output: '',
        activities: '',
        months: {
          M1: false, M2: false, M3: false, M4: false, M5: false, M6: false,
          M7: false, M8: false, M9: false, M10: false, M11: false, M12: false
        }
      },
      {
        id: 5,
        objectives: '',
        expected_output: '',
        activities: '',
        months: {
          M1: false, M2: false, M3: false, M4: false, M5: false, M6: false,
          M7: false, M8: false, M9: false, M10: false, M11: false, M12: false
        }
      }
    ]);
    setSignatoryPrefs({
      prepared_by_enabled: true,
      checked_and_verified_enabled: true,
      approved_by_enabled: true
    });
    setError('');
    setSuccess('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePlanningRowChange = (rowId, field, value) => {
    setPlanningRows(prev => 
      prev.map(row => 
        row.id === rowId 
          ? { ...row, [field]: value }
          : row
      )
    );
  };

  const handleMonthToggle = (rowId, month) => {
    setPlanningRows(prev => 
      prev.map(row => 
        row.id === rowId 
          ? { 
              ...row, 
              months: { 
                ...row.months, 
                [month]: !row.months[month] 
              } 
            }
          : row
      )
    );
  };

  const addPlanningRow = () => {
    const newId = Math.max(...planningRows.map(row => row.id), 0) + 1;
    setPlanningRows(prev => [
      ...prev,
      {
        id: newId,
        objectives: '',
        expected_output: '',
        activities: '',
        months: {
          M1: false, M2: false, M3: false, M4: false, M5: false, M6: false,
          M7: false, M8: false, M9: false, M10: false, M11: false, M12: false
        }
      }
    ]);
  };

  const removePlanningRow = (rowId) => {
    if (planningRows.length > 1) {
      setPlanningRows(prev => prev.filter(row => row.id !== rowId));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Validate date range if both dates are provided
      if (form.planned_start && form.planned_end) {
        const startDate = new Date(form.planned_start);
        const endDate = new Date(form.planned_end);
        
        if (endDate <= startDate) {
          setError('Planned End date must be after Planned Start date');
          setLoading(false);
          return;
        }
      }

      // Prepare data for API call
      const workPlanData = {
        title: form.title,
        total_duration: form.total_duration,
        planned_start: form.planned_start,
        planned_end: form.planned_end,
        planning_table: planningRows,
        signatory_prefs: signatoryPrefs
      };
      
      // Only include status if user is Admin and editing an existing work plan
      if (user?.role === 'Admin' && workPlan?.work_plan_id && form.status) {
        workPlanData.status = form.status;
      }
      
      if (workPlan?.work_plan_id) {
        await apiClient.put(`${API_ENDPOINTS.WORK_PLANS}/${workPlan.work_plan_id}`, workPlanData);
        setSuccess('Work plan updated successfully!');
      } else {
        await apiClient.post(API_ENDPOINTS.WORK_PLANS, workPlanData);
        setSuccess('Work plan created successfully!');
      }

      // Call onSuccess callback and close modal after a short delay
      setTimeout(() => {
        if (onSuccess) onSuccess();
        handleClose();
      }, 1200);

    } catch (err) {
      console.error('Error saving work plan:', err);
      setError(err.response?.data?.error || 'Failed to save work plan. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onHide();
  };

  return (
    <Modal 
      show={show} 
      onHide={handleClose} 
      size="xl" 
      centered
      scrollable
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-calendar-check me-2"></i>
          {workPlan ? 'Edit Work Plan' : 'Create Work Plan'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto', padding: '1rem' }}>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')} className="mb-3">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {error}
          </Alert>
        )}

        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess('')} className="mb-3">
            <i className="bi bi-check-circle me-2"></i>
            {success}
          </Alert>
        )}

        <Form onSubmit={handleSubmit}>
          <Tabs
            id="work-plan-modal-tabs"
            activeKey={activeTab}
            onSelect={(k) => k && setActiveTab(k)}
            className="mb-3"
          >
            <Tab
              eventKey="basic"
              title={
                <span>
                  <i className="bi bi-info-circle me-2"></i>
                  Basic Information
                </span>
              }
            >
              <div className="pt-3">
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Title</Form.Label>
                  <Form.Control
                    type="text"
                    name="title"
                    value={form.title}
                    onChange={handleChange}
                    placeholder="Enter work plan title"
                    maxLength={500}
                    style={{ border: '1px solid #ced4da', padding: '10px' }}
                  />
                </Form.Group>

                <hr className="my-4" style={{ borderTop: '1px solid #dee2e6' }} />

                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Total Duration (in months)</Form.Label>
                  <Form.Control
                    type="text"
                    name="total_duration"
                    value={form.total_duration}
                    onChange={handleChange}
                    placeholder="e.g., 6 months, 1 year, 18 months"
                    maxLength={500}
                    style={{ border: '1px solid #ced4da', padding: '10px' }}
                  />
                </Form.Group>

                <hr className="my-4" style={{ borderTop: '1px solid #dee2e6' }} />

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">Planned Start</Form.Label>
                      <Form.Control
                        type="date"
                        name="planned_start"
                        value={form.planned_start}
                        onChange={handleChange}
                        style={{ border: '1px solid #ced4da', padding: '10px' }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">Planned End</Form.Label>
                      <Form.Control
                        type="date"
                        name="planned_end"
                        value={form.planned_end}
                        onChange={handleChange}
                        style={{ border: '1px solid #ced4da', padding: '10px' }}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {/* Status Field - Admin Only */}
                {user?.role === 'Admin' && workPlan && (
                  <>
                    <hr className="my-4" style={{ borderTop: '1px solid #dee2e6' }} />
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">Status</Form.Label>
                      <Form.Select
                        name="status"
                        value={form.status}
                        onChange={handleChange}
                        style={{ border: '1px solid #ced4da', padding: '10px' }}
                      >
                        <option value="Pending">Pending</option>
                        <option value="Checked and Verified by Extension Head">Checked and Verified by Extension Head</option>
                        <option value="Rejected by Extension Head">Rejected by Extension Head</option>
                        <option value="Approved by VC-RDES">Approved by VC-RDES</option>
                        <option value="Rejected by VC-RDES">Rejected by VC-RDES</option>
                      </Form.Select>
                      <Form.Text className="text-muted">Only Admin can change work plan status</Form.Text>
                    </Form.Group>
                  </>
                )}
              </div>
            </Tab>

            <Tab
              eventKey="details"
              title={
                <span>
                  <i className="bi bi-table me-2"></i>
                  Work Plan Details
                </span>
              }
            >
              <div className="pt-3">
                <div className="d-flex justify-content-end mb-2">
                  <Button type="button" variant="outline-primary" size="sm" onClick={addPlanningRow}>
                    <i className="bi bi-plus-lg me-1"></i>
                    Add Row
                  </Button>
                </div>

                <div
                  className="table-responsive"
                  style={{
                    overflowX: 'auto',
                    maxHeight: '400px',
                    overflowY: 'auto',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px'
                  }}
                >
                  <table className="table table-bordered table-sm" style={{ minWidth: '1200px', marginBottom: 0 }}>
                    <thead className="table-light sticky-top">
                      <tr>
                        <th
                          className="text-center"
                          style={{
                            width: '200px',
                            minWidth: '200px',
                            position: 'sticky',
                            left: 0,
                            backgroundColor: '#f8f9fa',
                            zIndex: 10
                          }}
                        >
                          Objectives
                        </th>
                        <th className="text-center" style={{ width: '200px', minWidth: '200px' }}>
                          Expected Output
                        </th>
                        <th className="text-center" style={{ width: '300px', minWidth: '300px' }}>
                          Activities
                        </th>
                        <th colSpan="12" className="text-center">
                          Y1
                        </th>
                        <th className="text-center" style={{ width: '70px', minWidth: '70px' }}>
                          Actions
                        </th>
                      </tr>
                      <tr>
                        <th style={{ position: 'sticky', left: 0, backgroundColor: '#f8f9fa', zIndex: 10 }}></th>
                        <th></th>
                        <th></th>
                        {['M1', 'M2', 'M3', 'M4', 'M5', 'M6', 'M7', 'M8', 'M9', 'M10', 'M11', 'M12'].map((month) => (
                          <th key={month} className="text-center small" style={{ width: '35px', fontSize: '0.75rem' }}>
                            {month}
                          </th>
                        ))}
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {planningRows.map((row) => (
                        <tr key={row.id}>
                          <td
                            style={{
                              width: '200px',
                              minWidth: '200px',
                              position: 'sticky',
                              left: 0,
                              backgroundColor: '#fff',
                              zIndex: 5,
                              padding: '8px',
                              border: '1px solid #dee2e6'
                            }}
                          >
                            <Form.Control
                              as="textarea"
                              rows="3"
                              value={row.objectives}
                              onChange={(e) => handlePlanningRowChange(row.id, 'objectives', e.target.value)}
                              placeholder="Enter objectives..."
                              style={{
                                border: '1px solid #ced4da',
                                resize: 'none',
                                width: '100%',
                                fontSize: '0.875rem',
                                padding: '8px'
                              }}
                            />
                          </td>
                          <td style={{ width: '200px', minWidth: '200px', padding: '8px', border: '1px solid #dee2e6' }}>
                            <Form.Control
                              as="textarea"
                              rows="3"
                              value={row.expected_output}
                              onChange={(e) => handlePlanningRowChange(row.id, 'expected_output', e.target.value)}
                              placeholder="Enter expected output..."
                              style={{
                                border: '1px solid #ced4da',
                                resize: 'none',
                                width: '100%',
                                fontSize: '0.875rem',
                                padding: '8px'
                              }}
                            />
                          </td>
                          <td style={{ width: '300px', minWidth: '300px', padding: '8px', border: '1px solid #dee2e6' }}>
                            <Form.Control
                              as="textarea"
                              rows="3"
                              value={row.activities}
                              onChange={(e) => handlePlanningRowChange(row.id, 'activities', e.target.value)}
                              placeholder="Enter activities..."
                              style={{
                                border: '1px solid #ced4da',
                                resize: 'none',
                                width: '100%',
                                fontSize: '0.875rem',
                                padding: '8px'
                              }}
                            />
                          </td>
                          {['M1', 'M2', 'M3', 'M4', 'M5', 'M6', 'M7', 'M8', 'M9', 'M10', 'M11', 'M12'].map((month) => (
                            <td
                              key={month}
                              className="text-center"
                              onClick={() => handleMonthToggle(row.id, month)}
                              style={{
                                cursor: 'pointer',
                                backgroundColor: row.months[month] ? '#dc3545' : 'transparent',
                                border: '1px solid #dee2e6',
                                minWidth: '35px',
                                width: '35px',
                                height: '50px',
                                transition: 'background-color 0.2s'
                              }}
                              title={row.months[month] ? 'Click to deselect' : 'Click to select'}
                            >
                              {row.months[month] && <i className="bi bi-check-lg text-white"></i>}
                            </td>
                          ))}
                          <td className="text-center" style={{ width: '70px', minWidth: '70px' }}>
                            {row.id > 5 && (
                              <Button
                                type="button"
                                variant="outline-danger"
                                size="sm"
                                onClick={() => removePlanningRow(row.id)}
                                title="Delete this row"
                              >
                                <i className="bi bi-trash"></i>
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <small className="text-muted mt-2 d-block">
                  <i className="bi bi-info-circle me-1"></i>
                  Click on month cells (M1-M12) to mark when activities will occur. Scroll horizontally to see all months.
                </small>
              </div>
            </Tab>

            <Tab
              eventKey="signatories"
              title={
                <span>
                  <i className="bi bi-pen me-2"></i>
                  Signatories
                </span>
              }
            >
              <div className="pt-3">
                <Alert variant="info" className="mb-3">
                  <i className="bi bi-info-circle me-2"></i>
                  Enable/disable which signatory sections will appear in the generated DOCX. Disabling a signatory will output no
                  name/designation/date/signature for that section.
                </Alert>

                {[
                  {
                    key: 'prepared_by_enabled',
                    id: 'wp-signatory-prepared-by',
                    label: 'Prepared by (Project Leader)'
                  },
                  {
                    key: 'checked_and_verified_enabled',
                    id: 'wp-signatory-checked-and-verified',
                    label: 'Checked and Verified (Extension Head)'
                  },
                  {
                    key: 'approved_by_enabled',
                    id: 'wp-signatory-approved-by',
                    label: 'Approved by (VC-RDES)'
                  }
                ].map((opt) => (
                  <div
                    key={opt.key}
                    role="checkbox"
                    aria-checked={!!signatoryPrefs[opt.key]}
                    tabIndex={0}
                    className="d-flex align-items-center gap-2 mb-3"
                    style={{
                      cursor: loading ? 'not-allowed' : 'pointer',
                      userSelect: 'none'
                    }}
                    onClick={() => {
                      if (loading) return;
                      toggleSignatoryPref(opt.key);
                    }}
                    onKeyDown={(e) => {
                      if (loading) return;
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleSignatoryPref(opt.key);
                      }
                    }}
                  >
                    <input
                      id={opt.id}
                      type="checkbox"
                      checked={!!signatoryPrefs[opt.key]}
                      readOnly
                      disabled={loading}
                      style={{
                        pointerEvents: 'none', // ensure ONE click path (container) toggles once
                        marginTop: 2
                      }}
                    />
                    <label
                      htmlFor={opt.id}
                      className="mb-0"
                      style={{ cursor: loading ? 'not-allowed' : 'pointer', marginLeft: 4 }}
                    >
                      {opt.label}
                    </label>
                  </div>
                ))}
              </div>
            </Tab>
          </Tabs>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button 
          variant="outline-secondary" 
          onClick={handleClose}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button 
          type="button" 
          variant="success" 
          onClick={(e) => {
            e.preventDefault();
            handleSubmit(e);
          }}
          disabled={loading}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              {workPlan ? 'Saving...' : 'Creating...'}
            </>
          ) : (
            <>
              <i className="bi bi-check-lg me-2"></i>
              {workPlan ? 'Save Changes' : 'Create Work Plan'}
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default WorkPlanModal;

